import type { Title } from '@sr-sdks/api-sdk-axios';
import {
  TITLES_DEFAULT_PARAMS,
  fetchTitlesPaginated,
} from '../../../api/titles/titlesApi';
import type { TitlesQueryParams } from '../../../api/titles/titlesTypes';
import { serviceBadgesFromTypes } from '../../../components/Badges/appBadgeConstants';
import { formatTitleType } from '../../../components/TitleCard/titleMeta';
import { createClientNameLookup } from '../../../utils/csv/clientNameLookup';
import { csvRequestConfig } from '../../../utils/csv/csvExport';
import {
  type CsvColumn,
  csvDate,
  csvJoin,
} from '../../../utils/csv/toCsvChunk';
import { ensureStableSort } from '../../../utils/helpers/sort';

/** Client names by id, for agent workspaces. */
export type TitlesCsvContext = { clientNames: Map<string, string> };

// The SDK types the nested records as `object`.
type Nested = Partial<
  Record<
    | 'capacity'
    | 'countryCode'
    | 'firstName'
    | 'genreCode'
    | 'languageCode'
    | 'lastName'
    | 'productionCompany'
    | 'title',
    string
  > & { originalTitle: boolean }
>;

const nested = (values?: object[]) => (values ?? []) as Nested[];

type TitlesCsvColumn = CsvColumn<Title, TitlesCsvContext>;

const col = (
  id: string,
  header: string,
  group: TitlesCsvColumn['group'],
  value: TitlesCsvColumn['value'],
): TitlesCsvColumn => ({ group, header, id, value });

/**
 * Every column the Titles download can offer, in file order. This is the
 * column allowlist: `ownerWorkspaceId`, `externalId`, `goldenTitleId`, the raw
 * serial and season ids, nested record ids and the soft-delete fields are left
 * out on purpose (APP-2436 column specs).
 */
export const titlesCsvColumns = [
  col('title', 'Title', 'default', (row) => row.title),
  col('year', 'Year', 'default', (row) => row.year),
  col('titleType', 'Program type', 'default', (row) =>
    formatTitleType(row.titleType),
  ),
  {
    ...col('client', 'Client', 'default', (row, ctx) =>
      csvJoin(row.ownerClientIds?.map((id) => ctx.clientNames.get(id))),
    ),
    agentOnly: true,
  },
  col('services', 'Services', 'default', (row) =>
    csvJoin(
      serviceBadgesFromTypes(row.serviceType ?? []).map(
        (meta) => meta.children,
      ),
    ),
  ),
  col('claimStatus', 'Claim status', 'default', (row) => row.claimStatus),
  col('titleId', 'Title ID', 'default', (row) => row._id),
  col('isan', 'ISAN', 'default', (row) => row.isan),
  col('claimType', 'Claim type', 'details', (row) => row.claimType),
  col('serialLevel', 'Serial level', 'details', (row) => row.serialLevel),
  col(
    'registrationType',
    'Registration type',
    'details',
    (row) => row.registrationType,
  ),
  col('seasonNo', 'Season no', 'details', (row) => row.seasonNo),
  col('episodeNo', 'Episode no', 'details', (row) => row.episodeNo),
  col('duration', 'Duration (min)', 'details', (row) => row.duration),
  col('fiction', 'Fiction', 'details', (row) => row.fiction),
  col('colour', 'Colour', 'details', (row) => row.colour),
  col('animation', 'Animation', 'details', (row) => row.animation),
  col('synopsis', 'Synopsis', 'details', (row) => row.synopsis),
  col('altTitles', 'Alternative titles', 'details', (row) =>
    csvJoin(
      nested(row.titles)
        .filter((title) => !title.originalTitle)
        .map((title) => title.title),
    ),
  ),
  col('countries', 'Countries', 'details', (row) =>
    csvJoin(nested(row.countries).map((country) => country.countryCode)),
  ),
  col('languages', 'Languages', 'details', (row) =>
    csvJoin(nested(row.languages).map((language) => language.languageCode)),
  ),
  col('genres', 'Genres', 'details', (row) =>
    csvJoin(nested(row.genres).map((genre) => genre.genreCode)),
  ),
  col('productionCompanies', 'Production companies', 'details', (row) =>
    csvJoin(
      nested(row.productionCompanies).map(
        (company) => company.productionCompany,
      ),
    ),
  ),
  col('interestedParties', 'Interested parties', 'details', (row) =>
    csvJoin(
      nested(row.interestedParties).map((party) => {
        const name = [party.firstName, party.lastName]
          .filter(Boolean)
          .join(' ');

        return party.capacity ? `${name} (${party.capacity})` : name;
      }),
    ),
  ),
  col(
    'workspaceName',
    'Workspace name',
    'details',
    (row) => row.ownerWorkspaceName,
  ),
  col('created', 'Created', 'details', (row) => csvDate(row.created)),
  col('updated', 'Updated', 'details', (row) => csvDate(row.updated)),
  col(
    'externalWorkReference',
    'External work reference',
    'ids',
    (row) => row.externalWorkReference,
  ),
  {
    ...col('clientIds', 'Client IDs', 'ids', (row) =>
      csvJoin(row.ownerClientIds),
    ),
    agentOnly: true,
  },
];

/** One export page of the list on screen, in a stable order. */
export const fetchTitlesCsvPage = (
  params: TitlesQueryParams,
  page: number,
  pageSize: number,
  signal: AbortSignal,
) =>
  fetchTitlesPaginated(
    {
      ...params,
      page,
      pageSize,
      // The `_id` tie-break stops rows with the same title from swapping
      // between pages mid-export.
      sortBy: ensureStableSort(TITLES_DEFAULT_PARAMS.sortBy),
    },
    csvRequestConfig(signal),
  );

/**
 * Resolves each page's owner client ids to names for agent workspaces. Built on
 * the shared lookup, so one export asks only for ids it hasn't seen yet.
 */
export const titlesCsvContext = (workspaceId: string, isAgent: boolean) => {
  const lookup = createClientNameLookup(workspaceId);

  return async (rows: Title[], signal: AbortSignal) => ({
    clientNames: isAgent
      ? await lookup(
          rows.flatMap((row) => row.ownerClientIds ?? []),
          signal,
        )
      : new Map<string, string>(),
  });
};
